import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Unlink } from 'lucide-react';
import { Button } from '../../ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../ui/dialog';
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from '../../ui/table';
import {
    getLinkedImplants,
    unlinkImplant,
} from '../../../services/api/policyService';
import { useToast } from '../../../hooks/use-toast';
import LoadingRing from '../../ui-components/LoadingRing';
import Status from '../../general-components/Status';
import SETTING_KEYS from '../../../constants/setting-keys';

interface LinkedImplantsDialogProps {
    policyId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    canManage: boolean;
    onCountChange?: (count: number) => void;
}

export default function LinkedImplantsDialog({
    policyId,
    open,
    onOpenChange,
    canManage,
    onCountChange,
}: LinkedImplantsDialogProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [implants, setImplants] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingImplantId, setPendingImplantId] = useState<string | null>(
        null
    );

    const fetchImplants = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getLinkedImplants(policyId);
            setImplants(data);
            onCountChange?.(data.length);
        } catch (err) {
            console.error('Failed to fetch linked implants:', err);
            setError(t('departmentPolicies.implantsDialog.failedToLoad'));
            toast({
                title: t('departmentPolicies.implantsDialog.failedToLoad'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchImplants();
        }
    }, [open, policyId]);

    const handleUnlink = async (implantId: string) => {
        setPendingImplantId(implantId);
        try {
            await unlinkImplant(policyId, implantId);
            setImplants((prev) => {
                const next = prev.filter((i) => i._id !== implantId);
                onCountChange?.(next.length);
                return next;
            });
        } catch (err) {
            console.error('Failed to unlink implant:', err);
            toast({
                title: t('departmentPolicies.implantsDialog.failedToUnlink'),
                variant: 'destructive',
            });
        } finally {
            setPendingImplantId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{t('departmentPolicies.implantsDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {canManage
                            ? t('departmentPolicies.implantsDialog.descriptionAdmin')
                            : t('departmentPolicies.implantsDialog.descriptionViewer')}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : error ? (
                    <p className="py-4 text-sm text-destructive">{error}</p>
                ) : implants.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                        {t('departmentPolicies.implantsDialog.noImplants')}
                    </p>
                ) : (
                    <div className="max-h-96 overflow-y-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('departmentPolicies.implantsDialog.implantName')}</TableHead>
                                    <TableHead>{t('departmentPolicies.implantsDialog.manufacturer')}</TableHead>
                                    <TableHead>{t('departmentPolicies.implantsDialog.status')}</TableHead>
                                    {canManage && (
                                        <TableHead className="text-right">
                                            {t('departmentPolicies.implantsDialog.action')}
                                        </TableHead>
                                    )}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {implants.map((implant) => (
                                    <TableRow key={implant._id}>
                                        <TableCell>
                                            <button
                                                className="text-sm font-medium hover:underline text-left"
                                                onClick={() => {
                                                    onOpenChange(false);
                                                    navigate(`/implant/${implant._id}`);
                                                }}
                                            >
                                                {implant[SETTING_KEYS.model]}
                                            </button>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {implant.manufacturer_id?.name ?? implant[SETTING_KEYS.manufacturer] ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Status status={implant[SETTING_KEYS.mriStatus]} small={true} />
                                        </TableCell>
                                        {canManage && (
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="hover:text-destructive"
                                                    disabled={
                                                        pendingImplantId ===
                                                        implant._id
                                                    }
                                                    onClick={() => handleUnlink(implant._id)}
                                                >
                                                    <Unlink className="h-3.5 w-3.5 mr-1.5" />
                                                    {pendingImplantId ===
                                                    implant._id
                                                        ? t('departmentPolicies.implantsDialog.unlinking')
                                                        : t('departmentPolicies.implantsDialog.unlink')}
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>
                        {t('departmentPolicies.implantsDialog.done')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
